import {
  CreateGoalRequestPayload,
  CreateProposalRequestPayload,
  Goal,
  GoalHolding,
  GoalWithHoldingSummary,
  Proposal,
  CreateProposalFund,
  ProposalReportData,
  ProposalBacktestResult,
} from "../../types";
import { PaginatedResponse } from "../../types/api";
import { StepupFrequency } from "../../constants";
import { api } from "../axios";

export function fetchGoals(
  clientId?: number
): Promise<PaginatedResponse<GoalWithHoldingSummary>> {
  return api.get("goals/", {
    params: { client: clientId },
  });
}

export function createGoal(payload: CreateGoalRequestPayload): Promise<Goal> {
  return api.post("goals/", payload);
}

export function updateGoal(
  id: number,
  payload: CreateGoalRequestPayload
): Promise<Goal> {
  return api.put(`goals/${id}/`, payload);
}

export function getGoal(id: number): Promise<Goal> {
  return api.get(`goals/${id}/`);
}

export function fetchGoalHoldings(id: number): Promise<GoalHolding[]> {
  return api.get(`goals/${id}/available-holdings/`);
}

export function syncGoalHoldings(payload: {
  id: number;
  holdings: { folio_id: number; scheme_isin: string }[];
}) {
  return api.post(`goals/${payload.id}/sync-holdings/`, {
    holdings: payload.holdings,
  });
}

export function fetchProposals(clientId: number): Promise<Proposal[]> {
  return api.get(`proposals/`, {
    params: {
      client: clientId,
    },
  });
}

export function getProposal(id: number): Promise<Proposal> {
  return api.get(`proposals/${id}/`);
}

export function createProposal(
  payload: CreateProposalRequestPayload
): Promise<Proposal> {
  return api.post(`proposals/`, payload);
}

export function updateProposal(
  id: number,
  payload: Partial<CreateProposalRequestPayload>
): Promise<Proposal> {
  return api.put(`proposals/${id}/`, payload);
}

export function updatePropsalFunds(
  proposalId: number,
  proposalFunds: CreateProposalFund[]
) {
  return api.post(`proposals/bulk_create/`, {
    proposal: proposalId,
    proposal_funds: proposalFunds,
  });
}

export function deleteProposal(id: number) {
  return api.delete(`proposals/${id}/`);
}

export function deleteGoal(id: number) {
  return api.delete(`goals/${id}/`);
}

export function fetchProposalReportData(
  id: number
): Promise<ProposalReportData> {
  return api.get(`proposals/${id}/pdf-data/`);
}

export function fetchGoalProposals(id: number): Promise<Proposal[]> {
  return api.get(`goals/${id}/proposals/`);
}

export function generatePropoposalReport(payload: { id: number; url: string }) {
  return api.post(`proposals/${payload.id}/generate-pdf/`, {
    url: payload.url,
  });
}

export function downloadProposalReport(id: number) {
  return api.get(`proposals/${id}/download-pdf/`, {
    responseType: "blob",
  });
}

// Step-up params override the proposal's saved step-up; omit them to use it
export function backtestProposal(payload: {
  id: number;
  period: string;
  sipFrequency: string;
  benchmark: string;
  stepUpFrequency?: StepupFrequency | null;
  stepUpAmount?: number | null;
  stepUpPercentage?: number | null;
}): Promise<ProposalBacktestResult> {
  const { id, stepUpFrequency, stepUpAmount, stepUpPercentage, ...rest } =
    payload;
  return api.get(`proposals/${id}/backtest/`, {
    params: {
      ...rest,
      step_up_frequency: stepUpFrequency ?? undefined,
      step_up_amount: stepUpAmount ?? undefined,
      step_up_percentage: stepUpPercentage ?? undefined,
    },
  });
}
